/**
 * Analytics helper functions for Lists component tracking (null state, and the
 * "Move lists into a project" entry point on the Lists summary page).
 * Following the schema pattern from NullView.txt guide
 */

/**
 * Creates and fires a standard analytics event
 * @param {Object} config - Analytics configuration
 * @param {string} config.eventName - Type of event ('click' or 'viewport impression')
 * @param {string} config.clickID - Unique identifier for the event
 * @param {Object} config.container - Container information
 * @param {Object} config.click - Click information (for click events)
 * @param {Object} config.impression - Impression information (for impression events)
 */
export const fireAnalyticsEvent = (config) => {
  if (typeof window !== 'undefined' && window.digitalData) {
    // Initialize digitalData.event array if it doesn't exist
    if (!window.digitalData.event) {
      window.digitalData.event = [];
    }

    const thdAnalyticsEvent = {
      category: { primaryCategory: 'batch' },
      eventInfo: { eventName: config.eventName },
      clickID: config.clickID,
      container: {
        component: 'Lists',
        category: '',
        name: 'Lists',
        id: 'lists-component',
        position: '',
        categoryPosition: '',
        type: 'content',
        ...config.container
      }
    };

    // Add click or impression specific data
    if (config.click) {
      thdAnalyticsEvent.click = {
        component: 'Lists',
        type: 'content',
        category: '',
        ...config.click
      };
    }

    if (config.impression) {
      thdAnalyticsEvent.impression = {
        component: 'Lists',
        type: 'content',
        category: '',
        ...config.impression
      };
    }

    window.digitalData.event.push(thdAnalyticsEvent);
  }
};

/**
 * Tracks clicks on Create List button in null state
 */
export const trackCreateListClick = () => {
  fireAnalyticsEvent({
    eventName: 'click',
    clickID: 'ck-_-Lists-_-lists-component-_-CreateListButton-_-1-_-lists-null-state',
    click: {
      id: 'lists-null-state',
      name: 'CreateListButton',
      position: 1
    }
  });
};

/**
 * Tracks clicks on Learn More button in null state
 */
export const trackLearnMoreClick = () => {
  fireAnalyticsEvent({
    eventName: 'click',
    clickID: 'ck-_-Lists-_-lists-component-_-LearnMoreButton-_-1-_-lists-null-state',
    click: {
      id: 'lists-null-state',
      name: 'LearnMoreButton',
      position: 1
    }
  });
};

/**
 * Tracks clicks on the "Choose a list" CTA in the "Move lists into a project" card.
 * Paired with the card's viewport impression (fired by useImpression) this gives CTR
 * for the entry point (DSCVR-4948 AC3). Conversion is tracked downstream by add-to,
 * which reports `projectStarter: 'copy from your lists'` when the project is created.
 */
export const trackMoveListsToProjectClick = () => {
  fireAnalyticsEvent({
    eventName: 'click',
    clickID: 'ck-_-Lists-_-lists-component-_-ChooseAListButton-_-1-_-lists-move-to-project',
    click: {
      id: 'lists-move-to-project',
      name: 'ChooseAListButton',
      position: 1
    }
  });
};

/**
 * Tracks dismissals of the "Move lists into a project" card, so the dismissal rate can be
 * read alongside CTR rather than showing up as unexplained impression drop-off.
 */
export const trackMoveListsToProjectDismiss = () => {
  fireAnalyticsEvent({
    eventName: 'click',
    clickID: 'ck-_-Lists-_-lists-component-_-DismissButton-_-1-_-lists-move-to-project',
    click: {
      id: 'lists-move-to-project',
      name: 'DismissButton',
      position: 1
    }
  });
};
